import { useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { AddressInput } from '@/components/Form/AddressInput/AddressInput';
import { Select } from '@/components/Form/Select';
import { TextInput } from '@/components/Form/TextInput';

import { COUNTRIES_WITH_POPULAR } from '@/constants/countries';
import { MinorAddressFormType } from '@/constants/minor';
import { ADDRESS_MAX_LENGTH } from '@/constants/places';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectLoggedInUser } from '@/redux/auth/selectors';
import { selectConfigsState } from '@/redux/configs/selectors';
import { selectOperationData } from '@/redux/operation';

import { OptionValue } from '@/types/components/common';
import { CountryISO } from '@/types/places';
import { AddressDetails } from '@/types/user';

import { isTrinidadOrTobagoUser } from '@/utils/user';

import { AddressFieldsStack } from '../MinorProofOfAddressDrawer.styled';

type MinorAddressFormFieldsProps = {
  disabled: boolean;
};

export function MinorAddressFormFields({
  disabled,
}: Readonly<MinorAddressFormFieldsProps>) {
  const { t, country: translatedCountry } = useTranslate();
  const { control, setValue, clearErrors } =
    useFormContext<MinorAddressFormType>();
  const [placeObject, setPlaceObject] = useState<AddressDetails | undefined>();

  const user = useAppSelector(selectLoggedInUser);
  const { user: minorUser } = useAppSelector(selectOperationData);
  const { addressCityOptions } = useAppSelector(selectConfigsState);

  const selectedCountry = useWatch({ control, name: 'country' });
  const line1 = useWatch({ control, name: 'line1' });

  const isTTUser = isTrinidadOrTobagoUser(user);
  const isSelectedCountryTT = selectedCountry === CountryISO.Trinidad;

  const isMinorCitizenshipTT = [minorUser?.citizenship, minorUser?.citizenship2]
    .filter(Boolean)
    .map(code => (code as string).toUpperCase())
    .includes(CountryISO.Trinidad);

  const hasCityList =
    !!addressCityOptions?.length && (!isTTUser || isSelectedCountryTT);

  useEffect(() => {
    if (typeof line1 === 'object') {
      setPlaceObject(line1 as AddressDetails);
    }
  }, [line1]);

  useEffect(() => {
    if (placeObject) {
      let line1City = placeObject.city;
      if (hasCityList) {
        line1City = addressCityOptions?.some(({ value }) => value === line1City)
          ? line1City
          : '';
      }
      setValue('city', line1City || '');
      setValue('state', placeObject.state || '');
      setValue('zip', placeObject.zip || '');
      setValue('country', placeObject.country || '');
      setValue('line1', placeObject.line1 || '');
      clearErrors(['city', 'state', 'zip', 'country', 'line1']);

      setPlaceObject(undefined);
    }
  }, [addressCityOptions, hasCityList, placeObject, setValue, clearErrors]);

  useEffect(() => {
    if (hasCityList && !disabled) {
      setValue('city', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountry]);

  const countryOptions = useMemo(
    () =>
      COUNTRIES_WITH_POPULAR.map(item => ({
        ...item,
        text: translatedCountry[item.value],
      })),
    [translatedCountry],
  );

  const cityOptions = useMemo(
    () => [
      ...(addressCityOptions || []),
      {
        text: t('createNewAccount.addressPage.form.city.noneOption'),
        value: OptionValue.None,
      },
    ],
    [addressCityOptions, t],
  );

  return (
    <AddressFieldsStack spacing={3}>
      <Controller
        name="line1"
        control={control}
        render={({ field, fieldState }) => (
          <AddressInput
            {...field}
            control={control}
            disabled={disabled}
            textFieldProps={{
              label: t('createNewAccount.addressPage.form.address.label'),
              error: !!fieldState.error,
              helperText:
                fieldState.error?.message ||
                (typeof field.value === 'string' &&
                  field.value.length >= ADDRESS_MAX_LENGTH.line1 &&
                  t('createNewAccount.addressPage.form.address.error.maximum', {
                    value: `${ADDRESS_MAX_LENGTH.line1}`,
                  })),
              inputProps: {
                maxLength: ADDRESS_MAX_LENGTH.line1,
              },
            }}
          />
        )}
      />

      {hasCityList && cityOptions ? (
        <Controller
          name="city"
          control={control}
          render={({ field, fieldState }) => (
            <Select
              {...field}
              control={control}
              options={cityOptions}
              label={t('createNewAccount.addressPage.form.city.label')}
              fullWidth
              disabled={disabled}
              useDrawerOnMobile
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
      ) : (
        <Controller
          name="city"
          control={control}
          render={({ field, fieldState }) => (
            <TextInput
              {...field}
              label={t('createNewAccount.addressPage.form.city.label')}
              fullWidth
              disabled={disabled}
              error={!!fieldState.error}
              helperText={
                fieldState.error?.message ||
                ((field.value?.length ?? 0) >= ADDRESS_MAX_LENGTH.city &&
                  t('createNewAccount.addressPage.form.city.error.maximum', {
                    value: `${ADDRESS_MAX_LENGTH.city}`,
                  }))
              }
              inputProps={{
                maxLength: ADDRESS_MAX_LENGTH.city,
              }}
            />
          )}
        />
      )}

      <Controller
        name="state"
        control={control}
        render={({ field, fieldState }) => (
          <TextInput
            {...field}
            label={t('createNewAccount.addressPage.form.state.label')}
            fullWidth
            disabled={disabled}
            error={!!fieldState.error}
            helperText={
              fieldState.error?.message ||
              ((field.value?.length ?? 0) >= ADDRESS_MAX_LENGTH.state &&
                t('createNewAccount.addressPage.form.state.error.maximum', {
                  value: `${ADDRESS_MAX_LENGTH.state}`,
                }))
            }
            inputProps={{
              maxLength: ADDRESS_MAX_LENGTH.state,
            }}
          />
        )}
      />

      <Controller
        name="zip"
        control={control}
        render={({ field, fieldState }) => (
          <TextInput
            {...field}
            label={t('createNewAccount.addressPage.form.zipCode.label')}
            fullWidth
            helperText={
              fieldState.error?.message ||
              ((field.value?.length ?? 0) >= ADDRESS_MAX_LENGTH.zip &&
                t('createNewAccount.addressPage.form.zipCode.error.maximum', {
                  value: `${ADDRESS_MAX_LENGTH.zip}`,
                }))
            }
            disabled={disabled}
            error={!!fieldState.error}
            inputProps={{
              maxLength: ADDRESS_MAX_LENGTH.zip,
            }}
          />
        )}
      />

      <Controller
        name="country"
        control={control}
        render={({ field, fieldState }) => (
          <Select
            {...field}
            control={control}
            options={countryOptions}
            label={t('createNewAccount.addressPage.form.country.label')}
            fullWidth
            disabled={disabled || (isTTUser && isMinorCitizenshipTT)}
            useDrawerOnMobile
            withSearchBar
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
          />
        )}
      />
    </AddressFieldsStack>
  );
}
